import { createComponentInstance } from '@/utils/mount-component'
import { inBrowser } from '@/utils'
import VideoInviteModal from './VideoInviteModal.vue'

let instance

const DEFAULT_OPTIONS = {
  inviter: {
    name: '',
    avatar: '',
    country: null,
  },
  invitationText: '', // Will use i18n translation if empty
  autoCloseSeconds: 30,
  showTimer: true,
  showClose: true,
  closeOnClickOverlay: false,
}

let currentOptions = Object.assign({}, DEFAULT_OPTIONS)

function initInstance() {
  instance = createComponentInstance(VideoInviteModal)
}

/**
 * Show video invite modal
 * @param {Object} options - Modal options
 * @param {Object} options.inviter - Inviter information
 * @param {string} options.inviter.name - Inviter name
 * @param {string} options.inviter.avatar - Inviter avatar URL
 * @param {Object} options.inviter.country - Inviter country info
 * @param {string} options.inviter.country.flag - Country flag emoji
 * @param {string} options.inviter.country.name - Country name
 * @param {string} options.invitationText - Invitation text message (optional, uses i18n if not provided)
 * @param {number} options.autoCloseSeconds - Auto close timeout in seconds (default: 30)
 * @param {boolean} options.showTimer - Show countdown timer (default: true)
 * @param {boolean} options.showClose - Show close button (default: true)
 * @param {boolean} options.closeOnClickOverlay - Allow close on overlay click (default: false)
 * @param {Function} options.onAccept - Callback when accept button is clicked
 * @param {Function} options.onReject - Callback when reject button is clicked
 * @param {Function} options.onTimeout - Callback when timeout occurs
 * @param {Function} options.onClose - Callback when close button is clicked
 * @returns {Promise<'accept' | 'reject' | 'timeout' | 'close'>}
 */
export function showVideoInvite(options) {
  if (!inBrowser) {
    return Promise.resolve(undefined)
  }

  return new Promise((resolve) => {
    if (!instance) {
      initInstance()
    }

    const mergedOptions = Object.assign({}, currentOptions, options)

    // Wrap callbacks to resolve promise
    const originalOnAccept = mergedOptions.onAccept
    const originalOnReject = mergedOptions.onReject
    const originalOnTimeout = mergedOptions.onTimeout
    const originalOnClose = mergedOptions.onClose

    instance.open(
      Object.assign({}, mergedOptions, {
        onAccept: () => {
          if (originalOnAccept) {
            originalOnAccept()
          }
          // instance.toggle(false)
          resolve('accept')
        },
        onReject: () => {
          if (originalOnReject) {
            originalOnReject()
          }
          instance.toggle(false)
          resolve('reject')
        },
        onTimeout: () => {
          if (originalOnTimeout) {
            originalOnTimeout()
          }
          instance.toggle(false)
          resolve('timeout')
        },
        onClose: () => {
          if (originalOnClose) {
            originalOnClose()
          }
          instance.toggle(false)
          resolve('close')
        },
      })
    )
  })
}

/**
 * Close the currently displayed video invite modal
 */
export function closeVideoInvite() {
  if (instance) {
    instance.toggle(false)
  }
}
